import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { CiSelectComponent } from '../../../../profiler-theme';
import { IFaehigkeit } from '../../../shared/model/faehigkeit.model';
import {
  ISkillCategory,
  ISkillCreate,
} from '../../../shared/model/skill-management.model';
import { hasForbiddenCharacters } from '../../../shared/model/request.model';
import { FaehigkeitenService } from '../../../services/faehigkeiten.service';
import { MessageService } from '../../../shared/components/message.service';
import { INPUT_LIMITS } from '../../../shared/constants/input-limits';
import {
  compareText,
  hasText,
  problemType,
  sameText,
} from '../skill-catalog-model';
import { SkillIconComponent } from '../skill-icon/skill-icon.component';

type SkillFlag =
  | 'isProgrammiersprache'
  | 'isTechnologie'
  | 'isVorgehensmodell'
  | 'isWerkzeug';

interface ServerConflict {
  field: 'de' | 'en';
  category: string;
  requestedCategory: string;
  de: string;
  en: string;
}

@Component({
  selector: 'app-skill-add-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslatePipe,
    A11yModule,
    CiSelectComponent,
    SkillIconComponent,
  ],
  templateUrl: './skill-add-modal.component.html',
  styleUrl: './skill-add-modal.component.scss',
})
export class SkillAddModalComponent implements OnInit {
  private readonly skillService = inject(FaehigkeitenService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @Input() skills: IFaehigkeit[] = [];
  @Input() categories: ISkillCategory[] = [];
  @Input() preselectCategory: string | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() created = new EventEmitter<IFaehigkeit>();

  faehigkeit = '';
  faehigkeitEn = '';
  category: ISkillCategory | null = null;
  wikipage = '';
  flags: Record<SkillFlag, boolean> = {
    isProgrammiersprache: false,
    isTechnologie: false,
    isVorgehensmodell: false,
    isWerkzeug: false,
  };
  categoryOptions: ISkillCategory[] = [];
  saving = false;
  private serverConflict: ServerConflict | null = null;

  get limits() {
    return INPUT_LIMITS;
  }

  get flagOptions(): { flag: SkillFlag; label: string }[] {
    return [
      {
        flag: 'isProgrammiersprache',
        label: 'skillManagement.detail.flagProgrammingLanguage',
      },
      { flag: 'isTechnologie', label: 'skillManagement.detail.flagTechnology' },
      {
        flag: 'isVorgehensmodell',
        label: 'skillManagement.detail.flagProcessModel',
      },
      { flag: 'isWerkzeug', label: 'skillManagement.detail.flagTool' },
    ];
  }

  readonly categoryName = (category: ISkillCategory): string =>
    category.kategorie;

  readonly isCategoryDisabled = (category: ISkillCategory): boolean =>
    !hasText(category.kategorieEn);

  ngOnInit(): void {
    this.categoryOptions = [...this.categories].sort((a, b) =>
      compareText(a.kategorie, b.kategorie),
    );
    this.category =
      this.categoryOptions.find(
        (category) =>
          sameText(category.kategorie, this.preselectCategory) &&
          !this.isCategoryDisabled(category),
      ) ?? null;
  }

  get hasCategoriesWithoutEnglish(): boolean {
    return this.categoryOptions.some(this.isCategoryDisabled);
  }

  get duplicateDe(): IFaehigkeit | undefined {
    return this.duplicateIn(this.faehigkeit, (skill) => skill.faehigkeit);
  }

  get duplicateEn(): IFaehigkeit | undefined {
    return this.duplicateIn(this.faehigkeitEn, (skill) => skill.faehigkeitEn);
  }

  get duplicateDeCategory(): string | null {
    return (
      this.duplicateDe?.kategorie ?? this.conflictCategoryFor('de') ?? null
    );
  }

  get duplicateEnCategory(): string | null {
    return (
      this.duplicateEn?.kategorie ?? this.conflictCategoryFor('en') ?? null
    );
  }

  get conflictCategory(): string | null {
    return this.activeConflict?.category ?? null;
  }

  get otherCategories(): string[] {
    if (!hasText(this.faehigkeit)) {
      return [];
    }
    const names = this.skills
      .filter(
        (skill) =>
          sameText(skill.faehigkeit, this.faehigkeit) &&
          !sameText(skill.kategorie, this.category?.kategorie),
      )
      .map((skill) => skill.kategorie ?? '')
      .filter(hasText);
    return [...new Set(names)].sort(compareText);
  }

  get hasControlCharacters(): boolean {
    return [this.faehigkeit, this.faehigkeitEn, this.wikipage].some(
      hasForbiddenCharacters,
    );
  }

  get complete(): boolean {
    return (
      hasText(this.faehigkeit) && hasText(this.faehigkeitEn) && !!this.category
    );
  }

  get canCreate(): boolean {
    return (
      this.complete &&
      !this.duplicateDeCategory &&
      !this.duplicateEnCategory &&
      !this.hasControlCharacters &&
      !this.saving
    );
  }

  copyGermanToEnglish(): void {
    this.faehigkeitEn = this.faehigkeit;
  }

  toggleFlag(flag: SkillFlag): void {
    this.flags = { ...this.flags, [flag]: !this.flags[flag] };
  }

  create(): void {
    if (!this.canCreate || !this.category) {
      return;
    }
    const skill: ISkillCreate = {
      kategorie: this.category.kategorie,
      faehigkeit: this.faehigkeit.trim(),
      faehigkeitEn: this.faehigkeitEn.trim(),
      wikipage: this.wikipage.trim(),
      ...this.flags,
    };
    this.saving = true;
    this.skillService
      .createApproved(skill)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (saved) => {
          this.messageService.success(
            'skillManagement.add.created',
            undefined,
            {
              name: skill.faehigkeit,
            },
          );
          this.created.emit(saved);
        },
        error: (error: unknown) => this.handleError(error, skill),
      });
  }

  private duplicateIn(
    name: string,
    nameOf: (skill: IFaehigkeit) => string | undefined,
  ): IFaehigkeit | undefined {
    if (!hasText(name) || !this.category) {
      return undefined;
    }
    return this.skills.find(
      (skill) =>
        sameText(skill.kategorie, this.category?.kategorie) &&
        sameText(nameOf(skill), name),
    );
  }

  /** Ein Konflikt vom Server gilt nur, solange Namen und gewählte Kategorie unverändert sind. */
  private get activeConflict(): ServerConflict | null {
    const conflict = this.serverConflict;
    return conflict &&
      sameText(conflict.de, this.faehigkeit) &&
      sameText(conflict.en, this.faehigkeitEn) &&
      sameText(conflict.requestedCategory, this.category?.kategorie)
      ? conflict
      : null;
  }

  private conflictCategoryFor(field: 'de' | 'en'): string | undefined {
    const conflict = this.activeConflict;
    return conflict?.field === field ? conflict.category : undefined;
  }

  private handleError(error: unknown, skill: ISkillCreate): void {
    switch (problemType(error)) {
      case 'entity-name-already-exists':
        this.serverConflict = this.conflictOf(error, skill);
        return;
      case 'skill-category-unknown':
        this.messageService.error('skillManagement.detail.categoryUnknown');
        return;
      case 'entity-name-both-languages-required':
        this.messageService.error('skillManagement.add.bothLanguagesRequired');
        return;
      case 'constraint-violation':
        this.messageService.error('skillManagement.add.tooLong');
        return;
      default:
        this.messageService.error('skillManagement.add.saveError');
    }
  }

  private conflictOf(error: unknown, skill: ISkillCreate): ServerConflict {
    const parameters: Record<string, string | undefined> =
      (error instanceof HttpErrorResponse ? error.error?.parameters : null) ??
      {};
    const germanTaken = sameText(
      parameters['conflictingFaehigkeit'],
      skill.faehigkeit,
    );
    return {
      field: germanTaken ? 'de' : 'en',
      category: parameters['conflictingKategorie'] || skill.kategorie,
      requestedCategory: skill.kategorie,
      de: skill.faehigkeit,
      en: skill.faehigkeitEn,
    };
  }
}
